import {
  AttitudeState,
  CFDFile,
  DomainState,
  PtapLocation,
  RHRepo,
  RunConfigCase,
  SelectedSystem,
  SimulationState,
  SystemCode,
  TyreState,
  WingState
} from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { MultiSelect } from 'primereact/multiselect'
import { classNames } from 'primereact/utils'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { WorkflowSubmission } from '../../types/types'
import { useGetSimTemplates, fetchSimTemplatesForSubmission } from '../../api/submission-API'
import { useGetRHRepoSets } from '../../api/rhrepo-set-API'
import { protoService } from '../../proto/ProtoService'
import { generateUniqueId } from '../../utils/helpers'
import {
  applyRunLevelDefaultToCases,
  fillSimTemplateIfMissing
} from '../../utils/simTemplateOverride'
import { remapCasesToAttitudeSet } from '../../utils/attitudeSetCases'
import { isF1TeamState } from '../../utils/state-atoms'
import {
  CheckboxStyle,
  DropdownStyle,
  MultiSelectCheckboxStyle
} from '../../utils/styling-constants'
import Modal from '../Modal'
import CloseButton from '../buttons/CloseButton'
import SaveButton from '../buttons/SaveButton'
import CaseCreatorLine from './CaseCreatorLine'

const prefix = '|'

type NamePortion = {
  frh: string
  rrh: string
  yaw: string
  steer: string
  roll: string
  domain: string
  simulation: string
  fw: string
  drs: string
}

const formatFW = (fwNum: number | null) => {
  if (fwNum === null || fwNum === undefined) {
    return ''
  }
  let fw = fwNum.toFixed(1)
  fw = fw.replaceAll('.', 'p')

  return `${fw}FF`
}

const formatDRS = (drsNum: number | null) => {
  if (drsNum === null || drsNum === undefined) {
    return ''
  }
  let drs = drsNum.toFixed(0)
  return `${drs}RF`
}

type CreateRunConfigCase = {
  selectedAttitudeStates: AttitudeState[]
  cases: RunConfigCase[]
  selectedTyreState?: TyreState
  selectedDomainState?: DomainState
  selectedSimulationState?: SimulationState
  selectedWingState?: WingState
  selectedPtapLocation?: PtapLocation
  selectedRHRepo?: RHRepo
  selectedSystems?: SelectedSystem[]
  useDeformableTires?: boolean
  selectedSimTemplate?: CFDFile
}

type CaseCreatorModalProps = {
  mode?: string
  caseName?: string
  caseIndex?: number
  submitting: boolean
  submission: WorkflowSubmission
  inline?: boolean
  initialCases?: RunConfigCase[]
  onConfirm: (runConfigCases: RunConfigCase[]) => void
  onCancel: () => void
}

export type CaseCreatorHandle = {
  getProcessedCases: () => RunConfigCase[]
  /**
   * True while any per-case row's `.sim` upload is uploading, verifying, or failed (i.e. not yet
   * verified-and-attached). The inline Create-New-Run save path must block on this so a run is
   * never persisted with the previous/default SIM while an upload is still in flight.
   */
  hasPendingSimUploads: () => boolean
}

const CaseCreatorModal = forwardRef<CaseCreatorHandle, CaseCreatorModalProps>(
  ({ submitting, submission, inline = false, initialCases, onCancel, onConfirm }, ref) => {
    const [attitudeStates, setAttitudeStates] = useState<AttitudeState[]>(
      submission.runConfig.selectedAttitudeSet.attitudeStates || []
    )
    const [selectedAttitudeStateNames, setSelectedAttitudeStateNames] = useState<string[]>([])

    // Update attitude states when the selected attitude set changes
    const attitudeSetId = submission.runConfig.selectedAttitudeSet?._id
    useEffect(() => {
      setAttitudeStates(submission.runConfig.selectedAttitudeSet?.attitudeStates || [])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attitudeSetId])

    const [domainStates] = useState<DomainState[]>(
      submission.runConfig.selectedDomainSet?.domainStates || []
    )
    const [tyreSetStates] = useState<TyreState[]>(
      submission.runConfig.selectedTyreSet?.tyreStates || []
    )
    const [simulationStates] = useState<SimulationState[]>(
      submission.runConfig.selectedSimulationSet?.simulationStates || []
    )
    const [ptapLocationStates, setPtapLocationStates] = useState<PtapLocation[]>(
      submission.runConfig.selectedPtapLocationSet?.ptaplocationStates || []
    )

    // Update ptap location states when the selected ptap location set changes
    const ptapLocationSetId = submission.runConfig.selectedPtapLocationSet?._id
    useEffect(() => {
      setPtapLocationStates(submission.runConfig.selectedPtapLocationSet?.ptaplocationStates || [])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ptapLocationSetId])

    const [rhRepoStates, setRhRepoStates] = useState<RHRepo[]>(
      submission.runConfig.selectedRHRepoSet?.rhrepoStates || []
    )

    // Update RH repo states when the selected RH repo set changes
    const rhRepoSetId = submission.runConfig.selectedRHRepoSet?._id
    useEffect(() => {
      setRhRepoStates(submission.runConfig.selectedRHRepoSet?.rhrepoStates || [])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rhRepoSetId])

    // An existing run stores a snapshot of its RH repo set, which goes stale when RH files are
    // added later. Use the live set (same name-match a new run uses) when one exists.
    const [, fetchRHRepoSets] = useGetRHRepoSets()
    const attitudeSetName = submission.runConfig.selectedAttitudeSet?.name
    useEffect(() => {
      if (inline || !attitudeSetName) return
      let cancelled = false
      fetchRHRepoSets(`query=${encodeURIComponent(JSON.stringify({ name: attitudeSetName }))}`)
        .then((res: any) => {
          if (cancelled || !res?.data?.protoBytesList?.length) return
          const liveSet = protoService.decodeProtoResponse(res.data, 'rhreposet')[0]
          if (liveSet?.rhrepoStates?.length) {
            setRhRepoStates(liveSet.rhrepoStates)
          }
        })
        .catch(err => console.error('Error refreshing RH repo set:', err))
      return () => {
        cancelled = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inline, attitudeSetName])

    const [nameSegment, setNameSegment] = useState<NamePortion>({
      frh: '',
      rrh: '',
      yaw: '',
      steer: '',
      roll: '',
      fw: '',
      drs: '',
      domain: '',
      simulation: ''
    } as NamePortion)
    const [showAttitudeStateDialog, setShowAttitudeStateDialog] = useState(false)
    const [stateName, setStateName] = useState('')
    const {
      control,
      getValues,
      setValue,
      handleSubmit,
      formState: { errors }
    } = useForm<CreateRunConfigCase>()

    // Auto-populate the pressure tap state from the selected PTAP location set
    useEffect(() => {
      if (ptapLocationStates.length === 0) {
        return
      }
      const current = getValues('selectedPtapLocation')
      const next =
        (current && ptapLocationStates.find(state => state.name === current.name)) ??
        ptapLocationStates[0]
      if (next !== current) {
        setValue('selectedPtapLocation', next)
      }
    }, [ptapLocationStates, getValues, setValue])

    // Fetch sim templates for this submission to populate dropdown options and default selection
    const [{ data: simTemplatesData, loading: loadingSimTemplates }, fetchSimTemplates] =
      useGetSimTemplates()

    // Load initial sim templates for this submission. Need this to determine dropdown options and default selection based on methodology + any override from server
    useEffect(() => {
      const submissionId = submission._id
      if (submissionId) {
        fetchSimTemplatesForSubmission(fetchSimTemplates, submissionId, submission.name).catch(
          () => {}
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submission])

    useEffect(() => {
      const resp = simTemplatesData
      const opts: CFDFile[] = []
      if (resp) {
        // Build template options from methodology + override returned by server
        if (resp.templates && resp.templates.length) {
          resp.templates.forEach((t: any) => {
            opts.push(t as CFDFile)
          })
        }
        // Default selection priority: overrideSelectedSimFile -> first template
        if (submission.runConfig?.overrideSelectedSimFile) {
          const overrideName = submission.runConfig.overrideSelectedSimFile
          const found = opts.find(o => o.file === overrideName)
          if (found) {
            setValue('selectedSimTemplate', found)
          } else {
            setValue('selectedSimTemplate', {
              system: 'starccm',
              file: overrideName,
              path: (submission.runConfig as any)?.simOverrideDir || '',
              fileType: 'template',
              rootType: 'override'
            } as CFDFile)
          }
        } else if (opts.length) {
          setValue('selectedSimTemplate', opts[0])
        }
      }
    }, [submission, setValue, simTemplatesData])

    // For new runs (no submission ID), keep selectedSimTemplate in sync when the user
    // uploads an override sim file — the autoSelectFirstOption guard in SimTemplateDropdown
    // won't re-fire once a value is already set, so we need to set it explicitly here.
    const overrideFile = submission.runConfig?.overrideSelectedSimFile
    useEffect(() => {
      if (submission._id) return // existing submission — server fetch handles defaults
      if (overrideFile) {
        setValue('selectedSimTemplate', {
          system: 'starccm',
          file: overrideFile,
          path: (submission.runConfig as any)?.simOverrideDir || '',
          fileType: 'template',
          rootType: 'override'
        } as CFDFile)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overrideFile])

    // NOTE: SimTemplateDropdown encapsulates remote filtering and initial template fetch

    // Derive methodology templates for new runs (no submission ID)
    const methodologyTemplates = useMemo(() => {
      const templates: CFDFile[] = []

      // Add override sim file if present
      const overrideFile = submission.runConfig?.overrideSelectedSimFile
      if (overrideFile) {
        templates.push({
          system: 'starccm',
          file: overrideFile,
          path: (submission.runConfig as any)?.simOverrideDir || '',
          fileType: 'template',
          rootType: 'override'
        } as CFDFile)
      }

      // Add templates from methodology set
      const methodSet = submission.runConfig?.selectedMethodSet
      if (methodSet?.systemCodeFiles) {
        methodSet.systemCodeFiles
          .filter(
            (f: SystemCode) =>
              f.fileRef?.fileType === 'template' && f.systemType?.toLowerCase() === 'starccm'
          )
          .forEach((f: SystemCode) => {
            templates.push({
              system: 'starccm',
              file: f.fileName || f.fileRef?.file || '',
              path: f.fileRef?.path || '',
              fileType: 'template',
              rootType: 'methodology'
            } as CFDFile)
          })
      }

      // Add templates from master geometry run
      const masterGeoTmpls = (submission.runConfig as any)?.masterGeoTemplates
      if (masterGeoTmpls?.length) {
        masterGeoTmpls.forEach((t: any) => {
          // Avoid duplicates
          if (!templates.some(existing => existing.file === t.file)) {
            templates.push({
              system: t.system || 'starccm',
              file: t.file,
              path: t.path || '',
              fileType: t.fileType || 'template',
              rootType: t.rootType || 'master-geometry'
            } as CFDFile)
          }
        })
      }

      return templates
    }, [
      submission.runConfig?.selectedMethodSet,
      submission.runConfig?.overrideSelectedSimFile,
      (submission.runConfig as any)?.masterGeoTemplates
    ])

    const { fields: cases } = useFieldArray({
      control,
      name: 'cases'
    })

    // Per-case .sim upload readiness, keyed by case name (the row's React key). A row is "pending"
    // from upload start through verification and through an unresolved failure — i.e. until the
    // selected template is actually verified in Azure and attached to the case.
    const pendingSimRowsRef = useRef<Set<string>>(new Set())
    const handleSimReadinessChange = useCallback((caseName: string, pending: boolean) => {
      if (pending) pendingSimRowsRef.current.add(caseName)
      else pendingSimRowsRef.current.delete(caseName)
    }, [])

    const isF1Team = useRecoilValue(isF1TeamState)
    const labelRequiredByF1Team = classNames('mb-2', { 'text-gray-400': !isF1Team })

    // The Add Cases modal no longer shows a Sim Template picker (the run-level Override Template
    // Sim File and the per-case row uploads cover it). Keep the run-level default sim in sync for
    // new runs: a run-level override (uploaded or picked) wins, else the methodology template.
    // Applied to every case except deliberate per-case uploads, and re-applied when the user
    // switches the Methodology Set or changes the override — so switching methodology or uploading
    // a run-level override propagates to the cases.
    useEffect(() => {
      if (submission._id) return // existing submission — server fetch handles defaults
      if (!methodologyTemplates.length) return

      const overrideEntry = methodologyTemplates.find(
        t => (t.rootType || '').toLowerCase() === 'override'
      )
      const methodologyDefault = methodologyTemplates.find(t =>
        (t.rootType || '').toLowerCase().startsWith('methodolog')
      )
      const runLevelDefault = overrideEntry || methodologyDefault || null
      if (!runLevelDefault) return

      const currentTop = getValues('selectedSimTemplate') as CFDFile | undefined
      if (
        !currentTop ||
        currentTop.file !== runLevelDefault.file ||
        (currentTop.path || '') !== (runLevelDefault.path || '')
      ) {
        setValue('selectedSimTemplate', runLevelDefault)
      }

      const { cases: nextCases, changed } = applyRunLevelDefaultToCases(
        (getValues('cases') as RunConfigCase[]) || [],
        runLevelDefault
      )
      if (changed) setValue('cases', nextCases)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [methodologyTemplates])

    // Expose getProcessedCases for parent to call on save (inline mode)
    useImperativeHandle(
      ref,
      () => ({
        getProcessedCases: () => {
          const formData = getValues()
          const cases = formData.cases || []
          // Copy common data to each case
          cases.forEach(caseItem => {
            formData.selectedDomainState &&
              (caseItem.selectedDomainState = { ...formData.selectedDomainState } as DomainState)
            formData.selectedSimulationState &&
              (caseItem.selectedSimulationState = {
                ...formData.selectedSimulationState
              } as SimulationState)
            formData.selectedTyreState &&
              (caseItem.selectedTyreState = { ...formData.selectedTyreState } as TyreState)
            formData.selectedPtapLocation &&
              (caseItem.selectedPtapLocation = { ...formData.selectedPtapLocation } as PtapLocation)
            formData.selectedSystems &&
              (caseItem.selectedSystems = [...formData.selectedSystems] as SelectedSystem[])
            fillSimTemplateIfMissing(caseItem, formData.selectedSimTemplate)
            caseItem.useDeformableTires = formData.useDeformableTires
          })
          return cases.filter(caseItem =>
            selectedAttitudeStateNames.includes(caseItem.selectedAttitudeState?.name || '')
          )
        },
        hasPendingSimUploads: () => pendingSimRowsRef.current.size > 0
      }),
      [getValues, selectedAttitudeStateNames]
    )

    const getFormErrorMessage = name => {
      return errors[name] ? (
        <small className="error">{errors[name]?.message}</small>
      ) : (
        <small className="error">&nbsp;</small>
      )
    }

    const onSubmit = async (formData: CreateRunConfigCase) => {
      if (!formData.selectedAttitudeStates || formData.selectedAttitudeStates.length === 0) {
        toast.error('At least one case must be created.')
        return
      }

      // Don't submit while a per-case .sim upload is still transferring/verifying in Azure, or if
      // one failed to verify — otherwise the case would be saved with the previous/default SIM.
      if (pendingSimRowsRef.current.size > 0) {
        toast.error(
          'A per-case SIM file is still uploading or failed to verify. Wait for it to be confirmed in Azure (or re-upload) before saving.'
        )
        return
      }

      // Check if any cases are missing RH repo matches (only for non-F1 teams)
      if (!isF1Team) {
        const casesWithoutRHRepo = formData.cases.filter(caseItem => {
          return (
            selectedAttitudeStateNames.includes(caseItem.selectedAttitudeState?.name || '') &&
            !caseItem.selectedRHRepo
          )
        })

        if (casesWithoutRHRepo.length > 0) {
          const caseNames = casesWithoutRHRepo.map(c => c.selectedAttitudeState?.name).join(', ')
          toast.error(
            `No matching RH files found for: ${caseNames}. Please check your attitude state data.`
          )
          return
        }
      }

      // copy common data to each case
      formData.cases.forEach((caseItem, caseIndex) => {
        formData.selectedDomainState &&
          (caseItem.selectedDomainState = { ...formData.selectedDomainState } as DomainState)
        formData.selectedSimulationState &&
          (caseItem.selectedSimulationState = {
            ...formData.selectedSimulationState
          } as SimulationState)
        formData.selectedTyreState &&
          (caseItem.selectedTyreState = { ...formData.selectedTyreState } as TyreState)
        formData.selectedPtapLocation &&
          (caseItem.selectedPtapLocation = { ...formData.selectedPtapLocation } as PtapLocation)
        formData.selectedSystems &&
          (caseItem.selectedSystems = [...formData.selectedSystems] as SelectedSystem[])
        fillSimTemplateIfMissing(caseItem, formData.selectedSimTemplate)
        caseItem.useDeformableTires = formData.useDeformableTires
      })

      const newCases = formData.cases
        .filter(caseItem => {
          return selectedAttitudeStateNames.includes(caseItem.selectedAttitudeState?.name || '')
        })
        .map(caseItem => {
          const attitude = caseItem.selectedAttitudeState as any
          if (attitude) {
            if (attitude.spec != null) attitude.spec = parseFloat(attitude.spec.toFixed(1))
            if (attitude.radBlocker != null)
              attitude.radBlocker = parseFloat(attitude.radBlocker.toFixed(1))
          }
          return caseItem
        })
      onConfirm && onConfirm(newCases)
    }

    const handleConfirm = () => {
      onCancel && onCancel()
    }

    const deleteCaseAtIndex = (caseIndex: number) => {
      const targetAttitudeState = attitudeStates[caseIndex]
      setSelectedAttitudeStateNames(prevNames =>
        prevNames.filter(name => name !== targetAttitudeState.name)
      )
      let selectedAttitudeStates = getValues('selectedAttitudeStates') || []
      setValue(
        'selectedAttitudeStates',
        selectedAttitudeStates.filter(state => state.name !== targetAttitudeState.name)
      )
    }

    // Attitude set and initialCases the case list was last built from.
    const appliedAttitudeSetIdRef = useRef(attitudeSetId)
    const appliedInitialCasesRef = useRef<RunConfigCase[]>()
    // In the row key, so rows remount (and re-match RH files) when the case list is replaced.
    const [casesVersion, setCasesVersion] = useState(0)

    useEffect(() => {
      // Pre-populate cases from initialCases (e.g. when duplicating a run).
      // Only act when there are actual initial cases to apply — never clear
      // user-added cases when initialCases is empty. The parent re-renders
      // (e.g. after a validation error) pass a fresh [] reference each time
      // which would otherwise wipe cases the user has already added.
      // Checked first: a duplicate changes the attitude set and the cases together.
      if (
        initialCases &&
        initialCases.length > 0 &&
        initialCases !== appliedInitialCasesRef.current
      ) {
        appliedInitialCasesRef.current = initialCases
        appliedAttitudeSetIdRef.current = attitudeSetId
        setValue('cases', initialCases)
        // One chip per attitude state, even if the seeded cases share one.
        const attitudeStateObjects = initialCases
          .map(c => c.selectedAttitudeState)
          .filter(
            (state, index, states) =>
              state?.name && states.findIndex(s => s?.name === state.name) === index
          ) as AttitudeState[]
        setSelectedAttitudeStateNames(attitudeStateObjects.map(state => state.name as string))
        setValue('selectedAttitudeStates', attitudeStateObjects)
        setCasesVersion(v => v + 1)
        return
      }

      if (attitudeSetId === appliedAttitudeSetIdRef.current) return
      appliedAttitudeSetIdRef.current = attitudeSetId
      // Attitude set switched: rebuild the selected cases from the new set's states.
      const nextStates = submission.runConfig.selectedAttitudeSet?.attitudeStates || []
      const { cases: remappedCases, removedNames } = remapCasesToAttitudeSet(
        getValues('cases') || [],
        selectedAttitudeStateNames,
        nextStates
      )
      const keptNames = Array.from(
        new Set(remappedCases.map(c => c.selectedAttitudeState?.name || ''))
      )
      setValue('cases', remappedCases)
      setValue(
        'selectedAttitudeStates',
        nextStates.filter(state => keptNames.includes(state.name))
      )
      setSelectedAttitudeStateNames(keptNames)
      setCasesVersion(v => v + 1)
      if (removedNames.length > 0) {
        toast(`Removed case(s) not in the selected attitude set: ${removedNames.join(', ')}`)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setValue, initialCases, attitudeSetId])

    const formContent = (
      <>
        <div className="relative">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full grid grid-cols-12 gap-x-2 gap-y-0 mt-8">
            {false && (
              <div className="col-span-12 flex flex-row items-center">
                <div>Attitude States</div>
                <div>
                  <Controller
                    name="selectedAttitudeStates"
                    rules={{ required: 'Attitude state is required.' }}
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <div className="flex justify-between">
                          <Button
                            type="button"
                            label="+ Create New State"
                            pt={{
                              root: {
                                className: 'pt-0 hidden'
                              }
                            }}
                            link
                            onClick={() => {
                              setShowAttitudeStateDialog(true)
                            }}
                          />
                        </div>
                        <Dropdown
                          disabled={false}
                          value={field.value}
                          pt={DropdownStyle}
                          onChange={e => {
                            // setSelectedAttitudeState(e.value)
                            // setBaseAttitudeState({ ...e.value })
                            setNameSegment({
                              frh: '',
                              rrh: '',
                              yaw: '',
                              steer: '',
                              roll: '',
                              fw: '',
                              drs: '',
                              domain: '',
                              simulation: ''
                            })
                            // handleAttitudeStateChange(e.value)
                            field.onChange(e.value)
                          }}
                          filter
                          filterBy="name"
                          className={classNames({ error: fieldState.error })}
                          options={attitudeStates}
                          optionValue="name"
                          optionLabel="name"
                          useOptionAsValue
                          placeholder="Select Starting State"
                          emptyMessage="&nbsp;&nbsp;No Data"
                        />
                        {getFormErrorMessage(field.name)}
                      </>
                    )}
                  />
                </div>
              </div>
            )}

            <div className="col-span-12 grid grid-cols-2 gap-x-4 items-center">
              <Controller
                // @ts-ignore
                name="selectedAttitudeStates"
                control={control}
                rules={{ required: 'State is required.' }}
                render={({ field, fieldState }) => (
                  <div className="flex items-center gap-x-2">
                    <label htmlFor={field.name} className="shrink-0 whitespace-nowrap">
                      Attitude States <span className="text-red-500">*</span>
                    </label>
                    <div className="flex-1">
                      <MultiSelect
                        id={field.name}
                        value={field.value}
                        options={attitudeStates}
                        optionLabel="name"
                        optionValue="name"
                        useOptionAsValue
                        appendTo={() => document.body}
                        onChange={e => {
                          field.onChange(e.value)
                          const newSelection = e.value as []
                          const selectedStateNames = newSelection.map(
                            (selectionItem: AttitudeState) => selectionItem.name as string
                          )
                          setSelectedAttitudeStateNames(selectedStateNames)

                          // Get current cases once at the beginning
                          let cases = getValues('cases') || []

                          newSelection.forEach((selectionItem: AttitudeState) => {
                            const stateName = selectionItem.name as string
                            const found = cases.find((caseItem: RunConfigCase) => {
                              return caseItem.selectedAttitudeState?.name === stateName
                            })

                            // if new selection
                            if (!found) {
                              const selectedAttitudeState = attitudeStates.find(
                                state => state.name === stateName
                              ) as AttitudeState
                              if (selectedAttitudeState) {
                                const topLevelSim = getValues('selectedSimTemplate')
                                const newCase: any = {
                                  key: generateUniqueId(),
                                  name: stateName,
                                  selectedAttitudeState: {
                                    ...(selectedAttitudeState || {})
                                  } as AttitudeState
                                }
                                if (topLevelSim) newCase.simTemplate = topLevelSim
                                cases.push(newCase as RunConfigCase)
                              }
                            }
                          })

                          // Update cases once at the end
                          setValue('cases', [...cases])
                        }}
                        emptyMessage="No states selected"
                        display="chip"
                        pt={{
                          ...MultiSelectCheckboxStyle,
                          root: {
                            className:
                              'flex-grow py-1 bg-transparent dark:border-neutral-70 dark:bg-transparent min-w-full'
                          },
                          token: {
                            className: '!py-0'
                          },
                          header: {
                            className: 'dark:bg-neutral-70 dark:border-neutral-70'
                          },
                          filterContainer: {
                            className: 'flex items-center basis-0 grow'
                          },
                          filterIcon: {
                            className: 'right-2'
                          },
                          filterInput: {
                            root: {
                              className:
                                '!py-1 min-w-full bg-transparent dark:bg-transparent dark:!border-neutral-10'
                            }
                          },
                          wrapper: {
                            className: 'dark:bg-neutral-70 dark:border-neutral-70'
                          },
                          item: {
                            className: 'py-2 dark:bg-neutral-70'
                          },
                          emptyMessage: {
                            className: 'px-4'
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              />
              {/* Pressure Tap State - inline with Attitude States */}
              {!isF1Team && (
                <Controller
                  name="selectedPtapLocation"
                  rules={{ required: false }}
                  control={control}
                  render={({ field, fieldState }) => (
                    <div className="flex items-center gap-x-2">
                      <label htmlFor={field.name} className="shrink-0 whitespace-nowrap">
                        Pressure Tap State
                      </label>
                      <div className="flex-1">
                        <Dropdown
                          value={field.value}
                          pt={DropdownStyle}
                          onChange={e => {
                            field.onChange(e.value)
                          }}
                          className={classNames('w-full', { error: fieldState.error })}
                          options={ptapLocationStates}
                          optionLabel="name"
                          useOptionAsValue
                          placeholder="Select PTAP Location State"
                          emptyMessage="&nbsp;&nbsp;No Data"
                        />
                      </div>
                    </div>
                  )}
                />
              )}
            </div>

            {/* spacer */}
            <div className="h-8 col-span-12"></div>

            {cases &&
              cases.length > 0 &&
              cases.map((caseItem, caseIndex) => {
                if (
                  !selectedAttitudeStateNames.includes(caseItem.selectedAttitudeState?.name || '')
                ) {
                  return null
                }
                return (
                  <CaseCreatorLine
                    key={`${casesVersion}|${caseItem.name}`}
                    submitting={submitting}
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                    caseName={caseItem.name}
                    caseIndex={caseIndex}
                    caseItem={caseItem}
                    rhrepoStates={rhRepoStates}
                    onDelete={deleteCaseAtIndex}
                    onSimReadinessChange={handleSimReadinessChange}
                  />
                )
              })}

            {selectedAttitudeStateNames.length === 0 && (
              <div className="flex items-center justify-center col-span-12 py-8">
                No cases added. Please select attitude states to create cases.
              </div>
            )}

            {/* spacer */}
            <div className="h-6 col-span-12"></div>

            {/* Domain State */}
            <div className="field-box col-span-3">
              <Controller
                name="selectedDomainState"
                rules={{ required: isF1Team ? 'Domain state is required.' : false }}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className={labelRequiredByF1Team}>
                      Domain State {isF1Team && <span className="text-red-500">*</span>}
                    </label>
                    <Dropdown
                      disabled={!isF1Team}
                      value={field.value}
                      pt={DropdownStyle}
                      onChange={e => {
                        field.onChange(e.value)
                        setNameSegment({
                          ...nameSegment,
                          domain: prefix + e.value.name
                        })
                      }}
                      className={classNames({ error: fieldState.error })}
                      options={domainStates}
                      optionValue="name"
                      optionLabel="name"
                      useOptionAsValue
                      placeholder="Select Starting State"
                      emptyMessage="&nbsp;&nbsp;No Data"
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>

            {/* Tire State */}
            <div className="field-box col-span-3">
              <Controller
                name="selectedTyreState"
                rules={{ required: isF1Team ? 'Tire state is required.' : false }}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className={labelRequiredByF1Team}>
                      Tire State {isF1Team && <span className="text-red-500">*</span>}
                    </label>
                    <Dropdown
                      disabled={!isF1Team}
                      value={field.value}
                      pt={DropdownStyle}
                      onChange={e => {
                        field.onChange(e.value)
                      }}
                      className={classNames({ error: fieldState.error })}
                      options={tyreSetStates}
                      optionValue="name"
                      optionLabel="name"
                      useOptionAsValue
                      placeholder="Select Starting State"
                      emptyMessage="&nbsp;&nbsp;No Data"
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>

            {/* Simulation State */}
            <div className="field-box col-span-3">
              <Controller
                name="selectedSimulationState"
                rules={{ required: isF1Team ? 'Simulation state is required.' : false }}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className={labelRequiredByF1Team}>
                      Simulation State {isF1Team && <span className="text-red-500">*</span>}
                    </label>
                    <Dropdown
                      disabled={!isF1Team}
                      value={field.value}
                      pt={DropdownStyle}
                      onChange={e => {
                        field.onChange(e.value)
                        setNameSegment({
                          ...nameSegment,
                          simulation: prefix + e.value.name
                        })
                      }}
                      className={classNames({ error: fieldState.error })}
                      options={simulationStates}
                      optionValue="name"
                      optionLabel="name"
                      useOptionAsValue
                      placeholder="Select Starting State"
                      emptyMessage="&nbsp;&nbsp;No Data"
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>

            {/** fwState, hide for non-F1 teams */}
            <div className={classNames('field-box col-span-4', { '!hidden': !isF1Team })}>
              <Controller
                name="selectedWingState.fwState"
                control={control}
                disabled={submitting}
                rules={{ required: isF1Team ? 'FW State is required.' : false }}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      FW State {isF1Team && <span className="text-red-500">*</span>}
                    </label>
                    <InputNumber
                      id={field.name}
                      mode="decimal"
                      maxFractionDigits={1}
                      value={field.value}
                      inputClassName={classNames('w-full', { error: fieldState.error })}
                      onChange={e => {
                        field.onChange(e.value)
                        const fw = formatFW(e.value)
                        setNameSegment({
                          ...nameSegment,
                          fw
                        })
                      }}
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>

            {/** drs, hide for non-F1 teams */}
            <div className={classNames('field-box col-span-4', { '!hidden': !isF1Team })}>
              <Controller
                name="selectedWingState.drs"
                control={control}
                disabled={submitting}
                rules={{ required: isF1Team ? 'DRS% is required.' : '' }}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      DRS% {isF1Team && <span className="text-red-500">*</span>}
                    </label>
                    <InputNumber
                      id={field.name}
                      mode="decimal"
                      maxFractionDigits={1}
                      value={field.value}
                      inputClassName={classNames('w-full', { error: fieldState.error })}
                      onChange={e => {
                        field.onChange(e.value)
                        const drs = formatDRS(e.value)
                        setNameSegment({
                          ...nameSegment,
                          drs
                        })
                      }}
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>

            {/* Use Deformable Tires, hide for non-F1 teams */}
            <div className={classNames('field-box col-span-4', { '!hidden': !isF1Team })}>
              <Controller
                name="useDeformableTires"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <div className="flex-grow flex items-center">
                      <Checkbox
                        inputId={field.name}
                        pt={CheckboxStyle}
                        checked={field.value}
                        onChange={e => {
                          field.onChange(e.checked)
                        }}></Checkbox>
                      <label htmlFor={field.name} className="ml-2 font-normal">
                        Tire Deformation
                      </label>
                    </div>
                  </>
                )}
              />
            </div>

            {!inline && (
              <div className="w-full col-span-12 pt-8 flex items-center justify-end space-x-6">
                <SaveButton label="Save Case" loading={submitting} />
                <CloseButton label="Close" onClick={() => onCancel && onCancel()} />
              </div>
            )}
          </form>
        </div>
        <Dialog
          header="Create New Attitude State"
          visible={showAttitudeStateDialog}
          style={{ width: '500px' }}
          onHide={() => {
            if (!showAttitudeStateDialog) {
              return
            }
            setShowAttitudeStateDialog(false)
          }}
          footer={
            <div className="flex justify-end space-x-2">
              <Button
                label="Save"
                onClick={() => {
                  if (stateName) {
                    if (attitudeStates.find(state => state.name === stateName)) {
                      toast.error('Case name already exists.')
                      return
                    }
                    const newState = {
                      name: stateName,
                      frh: 0,
                      rrh: 0,
                      steer: 0,
                      roll: 0,
                      yaw: 0,
                      radius: 0,
                      speed: 0,
                      exhaust: 0
                    } as AttitudeState
                    setAttitudeStates([...attitudeStates, newState])
                    // setValue('selectedAttitudeState', newState)
                    // setSelectedAttitudeState(newState)
                    // setBaseAttitudeState(undefined)
                    setShowAttitudeStateDialog(false)
                    return
                  }
                  toast.error('State name is required.')
                }}
              />
              <CloseButton label="Close" onClick={() => setShowAttitudeStateDialog(false)} />
            </div>
          }>
          <InputText
            placeholder="Enter state name"
            pt={{
              root: {
                className: 'w-full'
              }
            }}
            onChange={e => {
              setStateName(e.target.value)
            }}
          />
        </Dialog>
      </>
    )

    if (inline) {
      return formContent
    }

    return (
      <Modal
        isOpen={true}
        withAction={false}
        easyClose={true}
        onCancel={() => onCancel && onCancel()}
        title="Add New Case(s)"
        showCancelButton={true}
        zIndex={30}
        onConfirm={handleConfirm}>
        {formContent}
      </Modal>
    )
  }
)

export default CaseCreatorModal
